/* eslint i18next/no-literal-string: 0 */
import React, { useEffect, memo } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import Popup from '../ui/popup'

import { RootState } from '../../store/rootReducer'
import { fixAppContainer, openMenu, pageLoaded, switchMenu, unfixAppContainer } from '../../store/common/commonSlice'
import isEmpty from 'lodash/isEmpty'
import './menu.sass'
import { Member } from '../../store/commonTypes'

import MenuButtonContainer from './menuButtonContainer'
import { useQueryParams } from '../../hooks/useQueryParams'
import Cookies from 'js-cookie'
import MenuMobile from './menuMobile'
import MenuNew from './newMenu/MenuNew'
import MenuNewLeft from './newMenu/MenuNewLeft'
import { useTranslation } from 'react-i18next'
import { createLocalizedPath } from '../../utils/localizationHelpers'
import paths from '../../paths'

const MenuLogin = React.lazy(() => import('./login'))
const MenuSignUp = React.lazy(() => import('./signUp'))
const MenuActivation = React.lazy(() => import('./activate'))
const MenuForgotPassword = React.lazy(() => import('./menuForgotPassword'))
const MenuChangePassword = React.lazy(() => import('./menuChangePassword'))

const Menu = () => {
    const location = useLocation()
    const dispatch = useDispatch()

    const { setQueryParam, removeMenuParams, getQueryParam, hasAnyQueryParam } = useQueryParams()
    const [t] = useTranslation()
    const loggedIn = useSelector((state: RootState) => state.member.loggedIn)

    const isMenuOpened = useSelector<RootState, boolean>((state) => state.common.isMenuOpened)
    const isChangingPassword = useSelector<RootState, boolean>((state) => state.common.isChangingPassword)
    const isAgeVerifOpened = useSelector<RootState, boolean>((state) => state.common.isAgeVerifOpened)

    const user = useSelector<RootState, Member | null>((state) => state.member.user)

    const hasAgeVerifOpened = React.useRef(false)

    type MenuForm = 'ageverif' | 'login' | 'register' | 'activate' | 'restore' | 'passwordChange' | null

    const getCurrentFormFromQuery = (): MenuForm => {
        if (isChangingPassword) return 'passwordChange'
        if (getQueryParam('ageverif')) {
            hasAgeVerifOpened.current = true
            return 'ageverif'
        }
        if (getQueryParam('password_change')) return isEmpty(user) ? 'login' : 'passwordChange'
        if (getQueryParam('restore')) return 'restore'
        if (getQueryParam('register')) return 'register'
        if (getQueryParam('activate')) return 'activate'
        if (!isEmpty(user) && user.active === 0) return 'activate'
        if (!Cookies.get('Visited')) return 'ageverif'
        if (getQueryParam('login') || !loggedIn) return 'login'
        return null
    }

    const switchForm = (form: MenuForm) => {
        removeMenuParams()
        setQueryParam(form)
    }

    const getCurrentMenu = () => {
        if (getCurrentFormFromQuery() === 'ageverif')
            return <MenuLogin switchForm={switchForm} hasAgeVerifOpened={hasAgeVerifOpened.current} />
        if (getCurrentFormFromQuery() === 'passwordChange') return <MenuChangePassword />
        if (getCurrentFormFromQuery() === 'restore') return <MenuForgotPassword switchForm={switchForm} />
        if (getCurrentFormFromQuery() === 'activate') return <MenuActivation switchForm={switchForm} user={user} />
        if (getCurrentFormFromQuery() === 'login') return <MenuLogin switchForm={switchForm} />
        if (getCurrentFormFromQuery() === 'register') return <MenuSignUp switchForm={switchForm} />
        if (!isEmpty(user) && !user.active) return <MenuLogin switchForm={switchForm} activate />
        return <MenuNewLeft user={user} />
    }

    useEffect(() => {
        return () => {
            hasAgeVerifOpened.current = false
        }
    }, [])

    useEffect(() => {
        if (
            hasAnyQueryParam(['ageverif', 'login', 'register', 'activate', 'restore', 'passwordChange']) ||
            isChangingPassword
        )
            dispatch(openMenu())
    }, [location])

    useEffect(() => {
        if (location.search.includes('delete-account')) {
            setTimeout(() => {
                dispatch(switchMenu())
                dispatch(pageLoaded())
                dispatch(fixAppContainer())
            }, 300)
        }
    }, [])

    return (
        <>
            <nav aria-hidden="true" style={{ display: 'none' }}>
                <a href={createLocalizedPath(paths.index.path)}>{t('Home')}</a>
                <a href={createLocalizedPath(paths.temporaryPhotos.path)}>{t('Ephemeral stories')}</a>
                <a href={createLocalizedPath(paths.flashing.path)}>{t('Flashing')}</a>
                <a href={createLocalizedPath(paths.indexPopular.path)}>{t('Top contributions')}</a>
                <a href={createLocalizedPath(paths.indexBestPhotos.path)}>{t('The best photos')}</a>
                <a href={createLocalizedPath('/tags/')}>{t('Search content')}</a>
                <a href={createLocalizedPath('/archive/')}>{t('Archive')}</a>

                <a href="/forum/">{t('Forum')}</a>
                <a href={createLocalizedPath(paths.profiles.path)}>{t('Profiles')}</a>
                <a href={createLocalizedPath(paths.indexCertifiedFemAndCouple.path)}>
                    {t('Certified females and couples')}
                </a>
                <a href={createLocalizedPath(paths.geo.path)}>{t('Around me')}</a>

                <a href={createLocalizedPath(paths.contact.path)}>{t('Contact')}</a>
                <a href={paths.cgu.path}>{t('CGU')}</a>
                <a href="https://www.bobtv.fr/">BobTV</a>

                <a href={'/'}>Fr</a>
                <a href={'/en/'}>En</a>
                <a href={'/ru/'}>Ru</a>
            </nav>

            {isMenuOpened ? (
                <Popup
                    onClose={() => {
                        dispatch(switchMenu())
                        dispatch(unfixAppContainer())
                        removeMenuParams()
                    }}
                    classname={`menu_main menu${isAgeVerifOpened ? ' menu_ageverif' : ''}`}
                >
                    <MenuNew getCurrentMenu={getCurrentMenu} />
                </Popup>
            ) : (
                <>
                    <MenuButtonContainer />
                    <MenuMobile />
                </>
            )}
        </>
    )
}

export default memo(Menu)
